import React from 'react';
import styled from 'styled-components';
import theme from '../theme';

/**
 * Wraps v1 Semantic UI pages inside the v2 layout with CSS overrides
 * to make them visually consistent with the v2 design system.
 * Used for pages that haven't been fully rewritten to v2 yet.
 */
const V2ContentWrapper = ({ children }) => {
  return (
    <ContentArea>
      {children}
    </ContentArea>
  );
};

const ContentArea = styled.div`
  font-family: ${theme.typography.fontFamily};

  /* Container — remove extra padding since v2 layout already handles it */
  .ui.container {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  /* Override componentContainerStyle inline margins */
  > div {
    margin-right: 0 !important;
  }

  /* MultiplyHeader / branded header — clean up */
  .ui.basic.segment {
    padding-top: 0 !important;
    margin-top: 0 !important;
  }

  h1.ui.icon.center.aligned.header,
  h1.ui.header.icon {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.h1.size} !important;
    color: ${theme.colors.text} !important;
    text-transform: none !important;
    font-weight: 700 !important;
    margin-bottom: ${theme.spacing.sm} !important;
    margin-top: 0 !important;
    text-align: left !important;
  }

  /* Hide logo icon in MultiplyHeader */
  h1.ui.icon.header > .icon,
  h1.ui.header.icon > .icon,
  h1.ui.icon.center.aligned.header > i.icon,
  .playbook-branded-header > .icon,
  .playbook-branded-header > i.icon,
  .playbook-branded-header > i {
    display: none !important;
  }

  /* Playbook branded header */
  .playbook-branded-header {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.h1.size} !important;
    color: ${theme.colors.text} !important;
    text-transform: none !important;
    font-weight: 700 !important;
    margin-bottom: ${theme.spacing.md} !important;
    margin-top: 0 !important;
    text-align: left !important;
  }

  /* Dividers — softer */
  .ui.divider {
    border-color: ${theme.colors.borderLight} !important;
  }

  /* Segments — match v2 card styling */
  .ui.segment:not(.basic) {
    border: none !important;
    box-shadow: ${theme.shadows.sm} !important;
    border-radius: ${theme.radii.md} !important;
    padding: ${theme.spacing.lg} !important;
    font-family: ${theme.typography.fontFamily};
  }

  .ui.segment.playbook-content-segment {
    background: ${theme.colors.surface};
  }

  /* Colored segments — subtle left border */
  .ui.yellow.segment,
  .ui.blue.segment,
  .ui.green.segment,
  .ui.orange.segment,
  .ui.teal.segment,
  .ui.red.segment,
  .ui.purple.segment {
    border-left: 4px solid !important;
    border-top: none !important;
    border-right: none !important;
    border-bottom: none !important;
  }

  .ui.yellow.segment { border-left-color: ${theme.colors.warning} !important; }
  .ui.blue.segment { border-left-color: ${theme.colors.info} !important; }
  .ui.green.segment { border-left-color: ${theme.colors.success} !important; }
  .ui.orange.segment { border-left-color: ${theme.colors.warning} !important; }
  .ui.teal.segment { border-left-color: ${theme.colors.primary} !important; }
  .ui.red.segment { border-left-color: ${theme.colors.danger} !important; }
  .ui.purple.segment { border-left-color: #8b5cf6 !important; }

  /* Headers — use v2 typography */
  .ui.header {
    font-family: ${theme.typography.fontFamily} !important;
    color: ${theme.colors.text};
  }

  h2.ui.header {
    font-size: ${theme.typography.h2.size} !important;
    font-weight: ${theme.typography.h2.weight} !important;
    margin-top: ${theme.spacing.lg} !important;
  }

  h3.ui.header {
    font-size: ${theme.typography.h3.size} !important;
    font-weight: ${theme.typography.h3.weight} !important;
  }

  h4.ui.header {
    font-size: ${theme.typography.body.size} !important;
    font-weight: 600 !important;
  }

  /* Colored headers */
  .ui.blue.header { color: ${theme.colors.info} !important; }
  .ui.green.header { color: ${theme.colors.success} !important; }
  .ui.orange.header { color: ${theme.colors.warning} !important; }
  .ui.teal.header { color: ${theme.colors.primary} !important; }
  .ui.red.header { color: ${theme.colors.danger} !important; }
  .ui.purple.header { color: #8b5cf6 !important; }

  /* Paragraphs */
  p {
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.body.size};
    line-height: 1.7;
    color: ${theme.colors.text};
  }

  /* Lists */
  .ui.list .item,
  .ui.list .list > .item {
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.body.size};
    line-height: 1.7;
    color: ${theme.colors.text};
  }

  /* Cards — match v2 card style */
  .ui.card,
  .ui.cards > .card {
    box-shadow: ${theme.shadows.sm} !important;
    border-radius: ${theme.radii.md} !important;
    border: none !important;
    font-family: ${theme.typography.fontFamily};
  }

  .ui.card > .content > .header,
  .ui.cards > .card > .content > .header {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.h3.size} !important;
    font-weight: 600 !important;
  }

  .ui.card > .content > .description,
  .ui.cards > .card > .content > .description {
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.small.size};
    line-height: 1.6;
    color: ${theme.colors.textSecondary};
  }

  /* Colored cards */
  .ui.red.card { border-top: 3px solid ${theme.colors.danger} !important; }
  .ui.yellow.card { border-top: 3px solid ${theme.colors.warning} !important; }
  .ui.green.card { border-top: 3px solid ${theme.colors.success} !important; }

  /* Tables — clean up */
  .ui.table {
    font-family: ${theme.typography.fontFamily} !important;
    border-radius: ${theme.radii.md} !important;
    border: none !important;
    box-shadow: none !important;
  }

  .ui.table thead th {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.small.size} !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    color: ${theme.colors.textSecondary} !important;
    background: ${theme.colors.surfaceHover} !important;
    border-bottom: 2px solid ${theme.colors.border} !important;
    padding: ${theme.spacing.sm} ${theme.spacing.md} !important;
  }

  .ui.table tbody td {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.body.size} !important;
    color: ${theme.colors.text} !important;
    padding: ${theme.spacing.sm} ${theme.spacing.md} !important;
    border-top: 1px solid ${theme.colors.borderLight} !important;
  }

  .ui.table tbody tr {
    transition: background ${theme.transitions.fast};
  }

  .ui.table tbody tr:hover {
    background: ${theme.colors.surfaceHover} !important;
  }

  /* Inputs — clean up */
  .ui.input input,
  .ui.input > input {
    font-family: ${theme.typography.fontFamily} !important;
    border-radius: ${theme.radii.sm} !important;
    border-color: ${theme.colors.border} !important;
    font-size: ${theme.typography.body.size} !important;
  }

  .ui.input input:focus {
    border-color: ${theme.colors.primary} !important;
    box-shadow: 0 0 0 2px ${theme.colors.primaryGhost} !important;
  }

  /* Dropdowns — minimal overrides to avoid breaking Semantic UI behavior */

  /* Buttons — primary buttons use theme color */
  .ui.primary.button,
  .ui.primary.buttons .button {
    background-color: ${theme.colors.primary} !important;
    font-family: ${theme.typography.fontFamily} !important;
    border-radius: ${theme.radii.sm} !important;
  }

  .ui.primary.button:hover {
    background-color: ${theme.colors.primaryDark} !important;
  }

  .ui.button {
    font-family: ${theme.typography.fontFamily} !important;
    border-radius: ${theme.radii.sm} !important;
  }

  /* Preserve circular button shape (grade indicators) */
  .ui.circular.button {
    border-radius: 50% !important;
  }

  /* Labels — keep inline */
  .ui.label {
    white-space: nowrap !important;
  }

  /* Forms */
  .ui.form .field > label {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.small.size} !important;
    font-weight: 600 !important;
    color: ${theme.colors.textSecondary} !important;
  }

  .ui.form input,
  .ui.form textarea {
    font-family: ${theme.typography.fontFamily} !important;
    border-radius: ${theme.radii.sm} !important;
  }

  /* Steps — clean up */
  .ui.steps {
    border: none !important;
    box-shadow: ${theme.shadows.sm} !important;
    border-radius: ${theme.radii.md} !important;
  }

  .ui.steps .step {
    font-family: ${theme.typography.fontFamily};
  }

  .ui.steps .step .title {
    font-family: ${theme.typography.fontFamily} !important;
    font-weight: 600 !important;
  }

  .ui.steps .step .description {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.small.size} !important;
  }

  /* Progress bars */
  .ui.progress {
    border-radius: ${theme.radii.sm} !important;
    font-family: ${theme.typography.fontFamily};
  }

  /* Grid */
  .ui.grid {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  /* Messages — base overrides handled below with feedback-specific styles */

  /* Links */
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    &:hover {
      color: ${theme.colors.primaryDark};
      text-decoration: underline;
    }
  }

  /* Playbook section cards */
  .playbook-section-card {
    transition: all ${theme.transitions.fast} !important;
    &:hover {
      box-shadow: ${theme.shadows.md} !important;
      transform: translateY(-1px);
    }
  }

  /* Grade indicator — shrink only inside table rows */
  .ui.table .ui.circular.button {
    width: 16px !important;
    height: 16px !important;
    padding: 0 !important;
    min-width: 0 !important;
    min-height: 0 !important;
    font-size: 0 !important;
  }

  /* Standalone grade button (assessment detail view) — keep natural size */
  .ui.massive.circular.button {
    box-shadow: none !important;
  }

  /* Feedback messages — improve readability */
  .ui.message {
    font-family: ${theme.typography.fontFamily} !important;
    font-size: ${theme.typography.body.size} !important;
    line-height: 1.7 !important;
    padding: ${theme.spacing.md} ${theme.spacing.lg} !important;
    border-radius: ${theme.radii.sm} !important;
    margin-bottom: ${theme.spacing.sm} !important;
    display: flex !important;
    align-items: flex-start !important;
    gap: ${theme.spacing.sm} !important;
  }

  .ui.message > .icon:not(.close) {
    font-size: 1em !important;
    flex-shrink: 0 !important;
    margin-top: 3px !important;
    margin-right: 0 !important;
  }

  .ui.message .content,
  .ui.message > :not(.icon):not(.close) {
    flex: 1 !important;
  }

  .ui.positive.message {
    background: #f0fdf4 !important;
    color: ${theme.colors.text} !important;
    border-left: 4px solid ${theme.colors.success} !important;
    border-top: none !important;
    border-right: none !important;
    border-bottom: none !important;
    box-shadow: none !important;
  }

  .ui.negative.message {
    background: #fef2f2 !important;
    color: ${theme.colors.text} !important;
    border-left: 4px solid ${theme.colors.danger} !important;
    border-top: none !important;
    border-right: none !important;
    border-bottom: none !important;
    box-shadow: none !important;
  }
`;

export default V2ContentWrapper;
